import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { Bounds, GizmoHelper, GizmoViewport, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

let sharedDracoLoader = null;
let meshoptReady = false;

function getSharedDracoLoader() {
  if (!sharedDracoLoader) {
    sharedDracoLoader = new DRACOLoader();
    sharedDracoLoader.setDecoderPath(`${process.env.PUBLIC_URL || ''}/draco/gltf/`);
  }
  return sharedDracoLoader;
}

function ensureMeshopt() {
  if (meshoptReady) return MeshoptDecoder;
  MeshoptDecoder.ready
    ?.then(() => {
      meshoptReady = true;
    })
    .catch(() => {});
  return MeshoptDecoder;
}

function formatBytes(n) {
  if (n == null || n === '') return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(mtime) {
  if (!mtime) return '—';
  try {
    return new Date(mtime * 1000).toLocaleString();
  } catch {
    return '—';
  }
}

function fmtInt(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Math.round(Number(n)).toLocaleString();
}

function fmtDim(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1000) return v.toFixed(1);
  if (abs >= 1) return v.toFixed(2);
  if (abs === 0) return '0';
  return v.toFixed(4);
}

function vec3Text(v) {
  if (!v) return '—';
  return `${fmtDim(v.x)} , ${fmtDim(v.y)} , ${fmtDim(v.z)}`;
}

function parseGlbContainer(buffer) {
  try {
    const view = new DataView(buffer);
    if (buffer.byteLength < 12) return null;
    const magic = view.getUint32(0, true);
    if (magic !== 0x46546c67) return null;
    const version = view.getUint32(4, true);
    const length = view.getUint32(8, true);
    const chunks = [];
    let offset = 12;
    while (offset + 8 <= buffer.byteLength) {
      const chunkLength = view.getUint32(offset, true);
      const chunkType = view.getUint32(offset + 4, true);
      const type =
        chunkType === 0x4e4f534a ? 'JSON' : chunkType === 0x004e4942 ? 'BIN' : `0x${chunkType.toString(16)}`;
      chunks.push({ type, length: chunkLength });
      offset += 8 + chunkLength;
    }
    return { version, length, chunks };
  } catch {
    return null;
  }
}

function collectTextureInfo(texture) {
  if (!texture) return null;
  const img = texture.image;
  const w = img?.width || img?.videoWidth || texture.source?.data?.width;
  const h = img?.height || img?.videoHeight || texture.source?.data?.height;
  return {
    name: texture.name || texture.source?.name || 'unnamed',
    size: w && h ? `${w} × ${h}` : '—',
    format: texture.format != null ? String(texture.format) : '—',
    encoding: texture.colorSpace || texture.encoding || '—',
  };
}

function collectGlbStats(gltf, extra = {}) {
  let vertices = 0;
  let triangles = 0;
  let meshes = 0;
  let skinnedMeshes = 0;
  let points = 0;
  let lines = 0;
  let bones = 0;
  let lights = 0;
  let camerasInScene = 0;
  let nodes = 0;
  let morphTargets = 0;
  let groups = 0;
  const geometryIds = new Set();
  const materialIds = new Set();
  const materials = [];
  const textures = [];
  const textureIds = new Set();
  const meshNames = [];
  const attributes = new Set();

  gltf.scene.traverse((obj) => {
    nodes += 1;
    if (obj.isGroup && !obj.isScene) groups += 1;
    if (obj.isBone) bones += 1;
    if (obj.isLight) lights += 1;
    if (obj.isCamera) camerasInScene += 1;
    if (obj.isPoints) {
      points += 1;
      const pos = obj.geometry?.attributes?.position;
      if (pos) vertices += pos.count;
    }
    if (obj.isLine || obj.isLineSegments) lines += 1;
    if (obj.isMesh || obj.isSkinnedMesh) {
      meshes += 1;
      if (obj.isSkinnedMesh) skinnedMeshes += 1;
      meshNames.push(obj.name || '(unnamed mesh)');
      const geo = obj.geometry;
      if (geo) {
        geometryIds.add(geo.uuid);
        Object.keys(geo.attributes || {}).forEach((k) => attributes.add(k));
        const pos = geo.attributes.position;
        if (pos) vertices += pos.count;
        if (geo.index) triangles += geo.index.count / 3;
        else if (pos) triangles += pos.count / 3;
        if (geo.morphAttributes?.position?.length) {
          morphTargets += geo.morphAttributes.position.length;
        }
      }
      const mats = Array.isArray(obj.material) ? obj.material : obj.material ? [obj.material] : [];
      mats.forEach((m) => {
        if (!m || materialIds.has(m.uuid)) return;
        materialIds.add(m.uuid);
        materials.push({
          name: m.name || '(unnamed)',
          type: m.type || m.constructor?.name || 'Material',
          color: m.color ? `#${m.color.getHexString()}` : '—',
          metalness: m.metalness,
          roughness: m.roughness,
          opacity: m.opacity,
          transparent: m.transparent,
          side: m.side === THREE.DoubleSide ? 'double' : m.side === THREE.BackSide ? 'back' : 'front',
        });
        [
          'map',
          'normalMap',
          'roughnessMap',
          'metalnessMap',
          'aoMap',
          'emissiveMap',
          'bumpMap',
          'displacementMap',
          'alphaMap',
          'lightMap',
          'envMap',
        ].forEach((key) => {
          const tex = m[key];
          if (!tex || textureIds.has(tex.uuid)) return;
          textureIds.add(tex.uuid);
          const info = collectTextureInfo(tex);
          if (info) textures.push({ ...info, slot: key });
        });
      });
    }
  });

  const box = new THREE.Box3().setFromObject(gltf.scene);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const json = gltf.parser?.json || {};
  const asset = json.asset || {};

  return {
    vertices,
    triangles: Math.round(triangles),
    meshes,
    skinnedMeshes,
    uniqueGeometries: geometryIds.size,
    materials: materials.length,
    textures: textures.length,
    points,
    lines,
    bones,
    lights,
    camerasInScene,
    nodes,
    groups,
    morphTargets,
    animations: gltf.animations?.length || 0,
    animationNames: (gltf.animations || []).map((a) => a.name || '(unnamed)'),
    animationDuration: (gltf.animations || []).reduce((max, a) => Math.max(max, a.duration || 0), 0),
    fileCameras: gltf.cameras?.length || 0,
    scenes: gltf.scenes?.length || 1,
    attributes: Array.from(attributes).sort(),
    meshNames: meshNames.slice(0, 40),
    meshNameCount: meshNames.length,
    materialList: materials.slice(0, 20),
    textureList: textures.slice(0, 20),
    boundingSize: size,
    boundingCenter: center,
    boundingMin: box.min.clone(),
    boundingMax: box.max.clone(),
    emptyBounds: box.isEmpty(),
    generator: asset.generator || '—',
    gltfVersion: asset.version || '—',
    minVersion: asset.minVersion || '—',
    copyright: asset.copyright || '—',
    extras: asset.extras ? JSON.stringify(asset.extras) : '—',
    extensionsUsed: json.extensionsUsed || [],
    extensionsRequired: json.extensionsRequired || [],
    jsonBuffers: json.buffers?.length || 0,
    jsonAccessors: json.accessors?.length || 0,
    jsonMeshes: json.meshes?.length || 0,
    jsonNodes: json.nodes?.length || 0,
    jsonMaterials: json.materials?.length || 0,
    jsonTextures: json.textures?.length || 0,
    jsonImages: json.images?.length || 0,
    jsonSkins: json.skins?.length || 0,
    ...extra,
  };
}

function disposeObject(root) {
  if (!root) return;
  root.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    const mats = Array.isArray(obj.material) ? obj.material : obj.material ? [obj.material] : [];
    mats.forEach((m) => {
      Object.keys(m).forEach((key) => {
        const v = m[key];
        if (v && v.isTexture) v.dispose();
      });
      m.dispose?.();
    });
  });
}

function PreviewModel({ url, onStats, onStatus }) {
  const [scene, setScene] = useState(null);
  const objectRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loader = new GLTFLoader();
    loader.setDRACOLoader(getSharedDracoLoader());
    const meshopt = ensureMeshopt();
    if (meshopt) loader.setMeshoptDecoder(meshopt);

    onStatus?.({ loading: true, error: null });
    setScene(null);

    (async () => {
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Failed to load (${res.status})`);
        const buffer = await res.arrayBuffer();
        if (cancelled) return;
        const container = parseGlbContainer(buffer);
        const gltf = await new Promise((resolve, reject) => {
          loader.parse(buffer, '', resolve, (err) =>
            reject(err instanceof Error ? err : new Error(err?.message || 'GLB parse failed'))
          );
        });
        if (cancelled) {
          disposeObject(gltf.scene);
          return;
        }
        const stats = collectGlbStats(gltf, {
          payloadBytes: buffer.byteLength,
          container,
        });
        objectRef.current = gltf.scene;
        setScene(gltf.scene);
        onStats?.(stats);
        onStatus?.({ loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        onStatus?.({ loading: false, error: err?.message || 'Failed to preview GLB' });
        onStats?.(null);
      }
    })();

    return () => {
      cancelled = true;
      if (objectRef.current) {
        disposeObject(objectRef.current);
        objectRef.current = null;
      }
      setScene(null);
    };
  }, [url, onStats, onStatus]);

  if (!scene) return null;
  return (
    <Bounds fit observe margin={1.35}>
      <primitive object={scene} />
    </Bounds>
  );
}

function CameraRig() {
  const { camera, gl } = useThree();
  useEffect(() => {
    camera.near = 0.01;
    camera.far = 1e8;
    camera.updateProjectionMatrix();
    gl.setClearColor('#09090b', 1);
  }, [camera, gl]);
  return null;
}

function StatRow({ label, value }) {
  if (value == null || value === '' || value === '—') return null;
  const display = Array.isArray(value) ? value.join(', ') : String(value);
  if (!display || display === '—') return null;
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-2 py-[3px] border-b border-zinc-900/80">
      <dt className="text-zinc-500 truncate">{label}</dt>
      <dd className="text-zinc-200 break-all text-right">{display}</dd>
    </div>
  );
}

function StatSection({ title, children }) {
  return (
    <section className="mb-3">
      <h4 className="text-[9px] uppercase tracking-[0.14em] text-yellow-600/80 mb-1">{title}</h4>
      <dl>{children}</dl>
    </section>
  );
}

export default function GlbPreviewPanel({ item, onClose }) {
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState({ loading: true, error: null });

  const onStats = useMemo(() => (next) => setStats(next), []);
  const onStatus = useMemo(() => (next) => setStatus(next), []);

  useEffect(() => {
    setStats(null);
    setStatus({ loading: true, error: null });
  }, [item?.url]);

  if (!item) return null;

  const container = stats?.container;

  return (
    <aside className="w-[min(420px,42vw)] shrink-0 border-l border-yellow-600/20 bg-zinc-950 flex flex-col min-h-0">
      <div className="h-8 px-2 border-b border-yellow-600/20 flex items-center justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <p className="text-[9px] uppercase tracking-[0.14em] text-yellow-500/85">Preview</p>
          <p className="text-[11px] text-zinc-200 truncate leading-tight">{item.name}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-zinc-700 text-zinc-400 rounded-sm hover:border-yellow-600/40 hover:text-yellow-400"
        >
          Close
        </button>
      </div>

      <div className="relative h-[min(42vh,320px)] shrink-0 bg-black border-b border-yellow-600/15">
        <Canvas
          gl={{ antialias: true, alpha: false, preserveDrawingBuffer: false }}
          camera={{ fov: 40, near: 0.01, far: 1e8, position: [8, 6, 8] }}
          dpr={[1, 1.75]}
        >
          <CameraRig />
          <ambientLight intensity={0.55} />
          <hemisphereLight args={['#fff7d6', '#1a1a1a', 0.45]} />
          <directionalLight position={[4, 8, 3]} intensity={1.15} />
          <directionalLight position={[-6, 2, -4]} intensity={0.45} color="#c4d4ff" />
          <Suspense fallback={null}>
            <PreviewModel key={item.url} url={item.url} onStats={onStats} onStatus={onStatus} />
          </Suspense>
          <OrbitControls makeDefault enableDamping dampingFactor={0.12} />
          <GizmoHelper alignment="bottom-right" margin={[48, 40]}>
            <GizmoViewport labelColor="white" axisHeadScale={1} />
          </GizmoHelper>
        </Canvas>
        {status.loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 pointer-events-none">
            <span className="text-[11px] text-yellow-400 uppercase tracking-wider">Loading GLB…</span>
          </div>
        )}
        {status.error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-4">
            <p className="text-[11px] text-red-300 text-center">{status.error}</p>
          </div>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-px bg-yellow-600/15 border-b border-yellow-600/15 shrink-0">
          {[
            ['Vertices', fmtInt(stats.vertices)],
            ['Triangles', fmtInt(stats.triangles)],
            ['Meshes', fmtInt(stats.meshes)],
          ].map(([label, value]) => (
            <div key={label} className="bg-zinc-950 px-2 py-1.5 text-center">
              <p className="text-[13px] font-semibold text-yellow-400 tabular-nums leading-tight">{value}</p>
              <p className="text-[8px] uppercase tracking-[0.12em] text-zinc-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto px-2.5 py-2 text-[10px]">
        <StatSection title="File">
          <StatRow label="Name" value={item.name} />
          <StatRow label="Path" value={item.path} />
          <StatRow label="Type" value="GLB (glTF binary)" />
          <StatRow label="Size on disk" value={formatBytes(item.size)} />
          <StatRow label="Payload" value={stats ? formatBytes(stats.payloadBytes) : null} />
          <StatRow label="Modified" value={formatDate(item.mtime)} />
        </StatSection>

        {container && (
          <StatSection title="GLB container">
            <StatRow label="Container ver." value={container.version} />
            <StatRow label="Byte length" value={formatBytes(container.length)} />
            {(container.chunks || []).map((c, i) => (
              <StatRow key={`${c.type}-${i}`} label={`Chunk ${i} (${c.type})`} value={formatBytes(c.length)} />
            ))}
          </StatSection>
        )}

        <StatSection title="Geometry">
          <StatRow label="Vertices" value={stats ? fmtInt(stats.vertices) : status.loading ? '…' : null} />
          <StatRow label="Triangles" value={stats ? fmtInt(stats.triangles) : null} />
          <StatRow label="Meshes" value={stats ? fmtInt(stats.meshes) : null} />
          <StatRow label="Skinned meshes" value={stats?.skinnedMeshes ? fmtInt(stats.skinnedMeshes) : null} />
          <StatRow label="Geometries" value={stats ? fmtInt(stats.uniqueGeometries) : null} />
          <StatRow label="Points objects" value={stats?.points ? fmtInt(stats.points) : null} />
          <StatRow label="Line objects" value={stats?.lines ? fmtInt(stats.lines) : null} />
          <StatRow label="Vertex attrs" value={stats?.attributes?.length ? stats.attributes.join(', ') : null} />
        </StatSection>

        <StatSection title="Scene">
          <StatRow label="Nodes" value={stats ? fmtInt(stats.nodes) : null} />
          <StatRow label="Groups" value={stats ? fmtInt(stats.groups) : null} />
          <StatRow label="Bones" value={stats?.bones ? fmtInt(stats.bones) : null} />
          <StatRow label="Morph targets" value={stats?.morphTargets ? fmtInt(stats.morphTargets) : null} />
          <StatRow label="Lights" value={stats?.lights ? fmtInt(stats.lights) : null} />
          <StatRow label="Cameras (scene)" value={stats?.camerasInScene ? fmtInt(stats.camerasInScene) : null} />
          <StatRow label="Cameras (file)" value={stats?.fileCameras ? fmtInt(stats.fileCameras) : null} />
          <StatRow label="Scenes" value={stats ? fmtInt(stats.scenes) : null} />
          <StatRow
            label="Animations"
            value={
              stats?.animations
                ? `${fmtInt(stats.animations)}${stats.animationDuration ? ` · ${stats.animationDuration.toFixed(2)}s` : ''}`
                : null
            }
          />
          <StatRow label="Clip names" value={stats?.animationNames?.length ? stats.animationNames : null} />
        </StatSection>

        {stats && !stats.emptyBounds && (
          <StatSection title="Bounds (scene units)">
            <StatRow
              label="Size X Y Z"
              value={`${fmtDim(stats.boundingSize.x)} × ${fmtDim(stats.boundingSize.y)} × ${fmtDim(stats.boundingSize.z)}`}
            />
            <StatRow label="Center" value={vec3Text(stats.boundingCenter)} />
            <StatRow label="Min" value={vec3Text(stats.boundingMin)} />
            <StatRow label="Max" value={vec3Text(stats.boundingMax)} />
          </StatSection>
        )}

        <StatSection title="Materials & textures">
          <StatRow label="Materials" value={stats ? fmtInt(stats.materials) : null} />
          <StatRow label="Textures" value={stats ? fmtInt(stats.textures) : null} />
          {stats?.materialList?.map((m, i) => (
            <StatRow
              key={`mat-${i}`}
              label={m.name}
              value={`${m.type}${m.color && m.color !== '—' ? ` · ${m.color}` : ''}${
                m.metalness != null ? ` · m${Number(m.metalness).toFixed(2)}` : ''
              }${m.roughness != null ? ` r${Number(m.roughness).toFixed(2)}` : ''}`}
            />
          ))}
          {stats?.textureList?.map((t, i) => (
            <StatRow key={`tex-${i}`} label={t.slot} value={`${t.name} · ${t.size}`} />
          ))}
        </StatSection>

        <StatSection title="glTF asset">
          <StatRow label="glTF version" value={stats?.gltfVersion} />
          <StatRow label="Min version" value={stats?.minVersion && stats.minVersion !== '—' ? stats.minVersion : null} />
          <StatRow label="Generator" value={stats?.generator && stats.generator !== '—' ? stats.generator : null} />
          <StatRow label="Copyright" value={stats?.copyright && stats.copyright !== '—' ? stats.copyright : null} />
          <StatRow label="JSON meshes" value={stats?.jsonMeshes ? fmtInt(stats.jsonMeshes) : null} />
          <StatRow label="JSON nodes" value={stats?.jsonNodes ? fmtInt(stats.jsonNodes) : null} />
          <StatRow label="JSON accessors" value={stats?.jsonAccessors ? fmtInt(stats.jsonAccessors) : null} />
          <StatRow label="JSON buffers" value={stats?.jsonBuffers ? fmtInt(stats.jsonBuffers) : null} />
          <StatRow label="JSON materials" value={stats?.jsonMaterials ? fmtInt(stats.jsonMaterials) : null} />
          <StatRow label="JSON textures" value={stats?.jsonTextures ? fmtInt(stats.jsonTextures) : null} />
          <StatRow label="JSON images" value={stats?.jsonImages ? fmtInt(stats.jsonImages) : null} />
          <StatRow label="JSON skins" value={stats?.jsonSkins ? fmtInt(stats.jsonSkins) : null} />
          <StatRow
            label="Extensions"
            value={stats?.extensionsUsed?.length ? stats.extensionsUsed.join(', ') : null}
          />
          <StatRow
            label="Required ext."
            value={stats?.extensionsRequired?.length ? stats.extensionsRequired.join(', ') : null}
          />
        </StatSection>

        {stats?.meshNames?.length > 0 && (
          <StatSection title={`Meshes (${fmtInt(stats.meshNameCount)})`}>
            {stats.meshNames.map((name, i) => (
              <StatRow key={`mesh-${i}`} label={`#${i + 1}`} value={name} />
            ))}
            {stats.meshNameCount > stats.meshNames.length && (
              <p className="text-zinc-600 pt-1">+{stats.meshNameCount - stats.meshNames.length} more</p>
            )}
          </StatSection>
        )}
      </div>
    </aside>
  );
}
